import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Box, IconButton, Tooltip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import { DataTable, type TableColumn, Loading, ErrorMessage } from '@/src/ui';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type {
    ClassStandardsReportSummaryDTO,
    QuestionsOnExamDTO,
} from '../models/classStandardsReportDTOs';
import type { StandardsReportFilterState } from '../../_shared/models/standardsReportFilterDTOs';
import StandardBreakdownModal from './StandardBreakdownModal';
import StandardQuestionsModal from './StandardQuestionsModal';
import CorrectnessBar from './CorrectnessBar';

interface StandardsTableProps {
    data: ClassStandardsReportSummaryDTO[];
    isLoading: boolean;
    error: string;
    emptyMessage?: string;
    examIds?: number[];
    // Map of examId → assessment name, used for the per-cell breakdown tooltip.
    examIdToName?: Record<number, string>;
    filterState: StandardsReportFilterState;
    pdfFileName?: string | (() => string);
}

interface ParentRow {
    isParent: true;
    standardId: number;
    standardCode: string;
    skillDescription: string;
    questionsOnExam: number;
    questionsOnExams: QuestionsOnExamDTO[];
    displayCorrect: number;
    displayTotal: number;
    displayPercent: number;
    studentsAssigned: number;
    studentsCompleted: number;
    hasSubstandards: boolean;
    substandardIds: number[];
}

interface ChildRow {
    isParent: false;
    parentStandardId: number;
    standardId: number;
    standardCode: string;
    skillDescription: string;
    questionsOnExam: number;
    questionsOnExams: QuestionsOnExamDTO[];
    displayCorrect: number;
    displayTotal: number;
    displayPercent: number;
}

// Sum the per-exam question counts across one or more rows.
const mergeQuestionsOnExams = (
    lists: Array<QuestionsOnExamDTO[] | undefined>
): QuestionsOnExamDTO[] => {
    const byExam = new Map<number, number>();
    for (const list of lists) {
        for (const entry of list ?? []) {
            byExam.set(entry.examId, (byExam.get(entry.examId) ?? 0) + entry.questionCount);
        }
    }
    return Array.from(byExam.entries()).map(([examId, questionCount]) => ({
        examId,
        questionCount,
    }));
};

type DisplayRow = ParentRow | ChildRow;

const codeCompare = (a: string, b: string) =>
    (a || '').localeCompare(b || '', undefined, { sensitivity: 'base', numeric: true });

// A substandard's code ends with a lowercase letter following a number, e.g.
// "5.NF.7a" → parent "5.NF.7". Standards without a trailing letter are treated
// as parents (or as standalone rows if no children match).
const SUBSTANDARD_CODE = /^(.+\.\d+)([a-z])$/;
const getParentCode = (code: string): string | null => {
    const m = (code || '').match(SUBSTANDARD_CODE);
    return m ? m[1] : null;
};

const tooltipHeaderStyle: React.CSSProperties = {
    textDecoration: 'underline dotted',
    textUnderlineOffset: 3,
    cursor: 'help',
};

const ClassStandardsReportTable: React.FC<StandardsTableProps> = ({
    data,
    isLoading,
    error,
    emptyMessage,
    examIds,
    examIdToName,
    filterState,
    pdfFileName,
}) => {
    const [questionsModalTarget, setQuestionsModalTarget] = useState<{
        standardId: number;
        standardCode: string;
    } | null>(null);

    const [selectedBreakdown, setSelectedBreakdown] = useState<{
        standardCode: string;
        standardIds: number[];
    } | null>(null);

    const [openMap, setOpenMap] = useState<Record<number, boolean>>({});
    const toggle = (id: number) => setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));

    const openQuestions = useCallback((standardId: number, standardCode: string) => {
        setQuestionsModalTarget({ standardId, standardCode });
    }, []);

    const rows: DisplayRow[] = useMemo(() => {
        const byCode = new Map<string, ClassStandardsReportSummaryDTO>();
        for (const row of data) byCode.set(row.standardCode, row);

        const childrenByParentCode = new Map<string, ClassStandardsReportSummaryDTO[]>();
        const isChild = new Set<string>();
        for (const row of data) {
            const parentCode = getParentCode(row.standardCode);
            if (parentCode && byCode.has(parentCode)) {
                isChild.add(row.standardCode);
                const list = childrenByParentCode.get(parentCode) ?? [];
                list.push(row);
                childrenByParentCode.set(parentCode, list);
            }
        }

        const parents = data
            .filter((r) => !isChild.has(r.standardCode))
            .sort((a, b) => codeCompare(a.standardCode, b.standardCode));

        const out: DisplayRow[] = [];
        for (const row of parents) {
            const children = (childrenByParentCode.get(row.standardCode) ?? []).sort((a, b) =>
                codeCompare(a.standardCode, b.standardCode)
            );

            const aggregatedCorrect =
                (row.questionsCorrect ?? 0) +
                children.reduce((sum, c) => sum + (c.questionsCorrect ?? 0), 0);
            const aggregatedTotal =
                (row.questionsTotal ?? 0) +
                children.reduce((sum, c) => sum + (c.questionsTotal ?? 0), 0);
            const aggregatedPercent =
                aggregatedTotal > 0 ? Math.round((aggregatedCorrect / aggregatedTotal) * 100) : 0;
            const aggregatedQuestionsOnExams = mergeQuestionsOnExams([
                row.questionsOnExams,
                ...children.map((c) => c.questionsOnExams),
            ]);
            const aggregatedQuestionsOnExam = aggregatedQuestionsOnExams.reduce(
                (s, e) => s + e.questionCount,
                0
            );

            out.push({
                isParent: true,
                standardId: row.standardId,
                standardCode: row.standardCode,
                skillDescription: row.skillDescription,
                questionsOnExam: aggregatedQuestionsOnExam,
                questionsOnExams: aggregatedQuestionsOnExams,
                displayCorrect: aggregatedCorrect,
                displayTotal: aggregatedTotal,
                displayPercent: aggregatedPercent,
                studentsAssigned: row.studentsAssigned,
                studentsCompleted: row.studentsCompleted,
                hasSubstandards: children.length > 0,
                substandardIds: children.map((c) => c.standardId),
            });

            if (children.length > 0 && openMap[row.standardId]) {
                for (const c of children) {
                    const childTotal = (c.questionsOnExams ?? []).reduce(
                        (s, e) => s + e.questionCount,
                        0
                    );
                    out.push({
                        isParent: false,
                        parentStandardId: row.standardId,
                        standardId: c.standardId,
                        standardCode: c.standardCode,
                        skillDescription: c.skillDescription,
                        questionsOnExam: childTotal,
                        questionsOnExams: c.questionsOnExams ?? [],
                        displayCorrect: c.questionsCorrect,
                        displayTotal: c.questionsTotal,
                        displayPercent: c.percentCorrect,
                    });
                }
            }
        }
        return out;
    }, [data, openMap]);

    const canViewQuestions = !!(examIds && examIds.length > 0);
    const linkTitle = canViewQuestions ? 'View questions' : 'Select an assessment first';
    const linkCursor = canViewQuestions ? 'pointer' : 'not-allowed';

    const renderStandardLink = (id: number, code: string) => (
        <Link
            to="#"
            onClick={(e) => {
                e.preventDefault();
                if (canViewQuestions) openQuestions(id, code);
            }}
            onContextMenu={(e) => e.preventDefault()}
            style={{ cursor: linkCursor }}
            title={linkTitle}
        >
            {code}
        </Link>
    );

    const columns: TableColumn[] = useMemo(
        () => [
            {
                dataKey: 'standardCode',
                header: 'Standard',
                allowSort: false,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => {
                    if (r.isParent) {
                        return (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {r.hasSubstandards ? (
                                    <IconButton
                                        size="small"
                                        aria-label={openMap[r.standardId] ? 'Collapse' : 'Expand'}
                                        onClick={() => toggle(r.standardId)}
                                        sx={{
                                            p: 0.25,
                                            color: 'inherit',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                color: 'black',
                                            },
                                        }}
                                    >
                                        {openMap[r.standardId] ? (
                                            <KeyboardArrowUp />
                                        ) : (
                                            <KeyboardArrowDown />
                                        )}
                                    </IconButton>
                                ) : (
                                    <Box sx={{ width: 28 }} />
                                )}
                                {renderStandardLink(r.standardId, r.standardCode)}
                            </Box>
                        );
                    }
                    return (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', pl: 4 }}>
                            {renderStandardLink(r.standardId, r.standardCode)}
                        </Box>
                    );
                },
            },
            {
                dataKey: 'skillDescription',
                header: 'Skill / Description',
                allowSort: false,
                align: 'left',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => r.skillDescription || '',
            },
            {
                dataKey: 'questionsOnExam',
                header: 'Questions',
                allowSort: false,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                renderHeader: () => (
                    <Tooltip
                        arrow
                        title="The number of questions on the selected assessment(s) tied to this standard. Hover over a cell for a per-assessment breakdown."
                    >
                        <span style={tooltipHeaderStyle}>Questions</span>
                    </Tooltip>
                ),
                formatter: (r: DisplayRow) => {
                    const total = r.questionsOnExam ?? 0;
                    const breakdown = r.questionsOnExams ?? [];
                    if (breakdown.length === 0) return `${total}`;
                    const tooltipBody = (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {breakdown.map((b) => (
                                <span key={b.examId}>
                                    {examIdToName?.[b.examId] ?? `Assessment ${b.examId}`}:{' '}
                                    {b.questionCount}
                                </span>
                            ))}
                        </Box>
                    );
                    return (
                        <Tooltip arrow title={tooltipBody}>
                            <span
                                style={{
                                    cursor: 'help',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textUnderlineOffset: 3,
                                }}
                            >
                                {total}
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                dataKey: 'displayCorrect',
                header: 'Correct',
                allowSort: false,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                renderHeader: () => (
                    <Tooltip
                        arrow
                        title="Questions linked to this standard that students answered correctly."
                    >
                        <span style={tooltipHeaderStyle}>Correct</span>
                    </Tooltip>
                ),
                formatter: (r: DisplayRow) => `${r.displayCorrect ?? 0}`,
            },
            {
                dataKey: 'displayTotal',
                header: 'Total',
                allowSort: false,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                renderHeader: () => (
                    <Tooltip
                        arrow
                        title="Total questions linked to this standard that students answered."
                    >
                        <span style={tooltipHeaderStyle}>Total</span>
                    </Tooltip>
                ),
                formatter: (r: DisplayRow) => `${r.displayTotal ?? 0}`,
            },
            {
                dataKey: 'displayPercent',
                header: 'Percent',
                allowSort: false,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => `${r.displayPercent ?? 0}%`,
            },
            {
                dataKey: 'studentsCompleted',
                header: 'Students Completed',
                allowSort: false,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) =>
                    r.isParent ? `${r.studentsCompleted ?? 0}/${r.studentsAssigned ?? 0}` : '',
            },
            {
                dataKey: 'bandDistribution',
                header: 'Distribution',
                allowSort: false,
                align: 'center',
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (r: DisplayRow) => {
                    const ids = r.isParent ? [r.standardId, ...r.substandardIds] : [r.standardId];
                    return (
                        <CorrectnessBar
                            percent={r.displayPercent ?? 0}
                            onClick={() =>
                                setSelectedBreakdown({
                                    standardCode: r.standardCode,
                                    standardIds: ids,
                                })
                            }
                        />
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [openMap, canViewQuestions, examIdToName]
    );

    if (isLoading) return <Loading message="Loading standards..." />;

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mb: 1 }}>
                <ErrorMessage error={error} />
            </Box>
        );
    }

    const hasData = data && data.length > 0;

    return (
        <>
            {hasData && (
                <div className="pl-4">
                    <h2
                        className="inline-block whitespace-nowrap text-lg px-2 py-2 pl-4 bg-[var(--pm-secondary-light)] text-[var(--pm-dark)] rounded-t-lg tracking-wide"
                        style={{
                            fontFamily: 'Verdana, Geneva, sans-serif',
                            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.4)',
                        }}
                    >
                        Class Standards Report
                    </h2>
                </div>
            )}
            <DataTable
                data={rows}
                columns={columns}
                tableName="ClassStandardsReportTable"
                emptyMessage={emptyMessage}
                rowsPerPageInitial={20}
                enableExportCSV
                enableExportExcel
                enableExportPDF
                pdfFileName={pdfFileName}
                alternatingRowColor
            />

            <StandardQuestionsModal
                open={questionsModalTarget !== null}
                onClose={() => setQuestionsModalTarget(null)}
                standardId={questionsModalTarget?.standardId ?? 0}
                standardCode={questionsModalTarget?.standardCode ?? ''}
            />

            {selectedBreakdown && (
                <StandardBreakdownModal
                    open={!!selectedBreakdown}
                    onClose={() => setSelectedBreakdown(null)}
                    standardCode={selectedBreakdown.standardCode}
                    standardIds={selectedBreakdown.standardIds}
                    filterState={filterState}
                />
            )}
        </>
    );
};

export default ClassStandardsReportTable;
